import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const clean = (email || "").trim().toLowerCase();
        if (!clean) return;
        setBusy(true);
        try {
            // We deliberately don't leak whether the email is registered —
            // the backend always returns 200. The UI just tells the user
            // to check their inbox.
            await api.post("/auth/forgot-password", {
                email: clean,
                origin: typeof window !== "undefined" ? window.location.origin : "",
            });
            setSent(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-[var(--bg)]">
            <div className="w-full max-w-md animate-in-up">
                <div className="flex items-center gap-3 mb-8">
                    <img src="/scs-icon-512.png" alt="SCS" className="h-12 w-12 rounded-2xl object-cover" />
                    <div className="leading-tight">
                        <div className="font-display text-lg font-semibold">SCS</div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[var(--terracotta)]">
                            Simple · Clear · Systems
                        </div>
                    </div>
                </div>

                {sent ? (
                    <div data-testid="forgot-sent">
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Check your email</h1>
                        <p className="mt-3 text-[var(--text-secondary)]">
                            If an account with that email exists, we've sent a reset link to <span className="font-medium text-[var(--text-primary)]">{email}</span>. Tap the button in the email to choose a new password.
                        </p>
                        <p className="mt-3 text-sm text-[var(--text-muted)]">
                            Don't see it? Check your spam folder, or ask your manager for a fresh sign-in link instead.
                        </p>
                        <Link data-testid="forgot-back-to-login" to="/login"
                              className="btn-primary mt-8 inline-flex items-center gap-2">
                            <ArrowLeft size={16} /> Back to sign in
                        </Link>
                    </div>
                ) : (
                    <>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="forgot-heading">
                            Forgotten your password?
                        </h1>
                        <p className="mt-2 text-[var(--text-secondary)] text-sm">
                            Enter the email you use to sign in and we'll send you a link to choose a new password.
                        </p>
                        <p className="mt-2 text-xs text-[var(--text-muted)]">
                            Phone-only members: this won't work for you — please ask your manager to send a fresh sign-in link on WhatsApp.
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">Email</label>
                                <div className="relative mt-1">
                                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                    <input data-testid="forgot-email-input"
                                           type="email"
                                           inputMode="email"
                                           autoCapitalize="none"
                                           autoCorrect="off"
                                           spellCheck={false}
                                           className="input-field pl-10"
                                           value={email}
                                           onChange={e => setEmail(e.target.value)}
                                           autoComplete="username" required />
                                </div>
                            </div>

                            <button data-testid="forgot-submit-btn" type="submit" disabled={busy}
                                    className="btn-primary w-full">
                                {busy ? "Sending…" : "Send reset link"}
                            </button>
                        </form>

                        <div className="mt-6 text-sm">
                            <Link data-testid="forgot-back-link" to="/login"
                                  className="text-[var(--pine)] font-medium hover:underline inline-flex items-center gap-1">
                                <ArrowLeft size={14} /> Back to sign in
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
